<script setup lang="ts">
import ArticleImage from './ArticleImage.vue';
import ArticleText from './ArticleText.vue';
import ArticleCategories from './ArticleCategories.vue';
import ArticleSummary from './ArticleSummary.vue';
import { formatDate } from '~/shared/utils/formatDate';

import ArticleRelative from './ArticleRelative.vue';

import type { MaterialDTO } from '~/shared/api/client/news/news.dto';
import type { ExpertNewsDTO } from '~/shared/api/client/expert-news/expert-news.dto';
import ArticleRelatedNews from './ArticleRelatedNews.vue';
import { isMaterialDTO } from '~/shared/lib/isMaterialDTO';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';
import ArticleGallery from './ArticleGallery.vue';
import ArticleBannerBoundary from '~/entities/article_banners/ui/ArticleBannerBoundary.vue';
import ArticleSpecial from './ArticleSpecial.vue';
import {
  SpecialProjectCmwp,
  SpecialProjectCre35Teaser,
} from '~/entities/special-project-extra';
import { ArticleBannerMiddle } from '~/entities/article_banners';

const props = defineProps<{
  item: MaterialDTO | ExpertNewsDTO;
  hideBack?: boolean;
  hideBanner?: boolean;
  index: number;
}>();

const handleClickFromWysiwyg = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (
    target.classList.contains('formaPartnerBlock__link') ||
    target.classList.contains('formaPartnerBlock__image')
  ) {
    (
      window as unknown as {
        ym: (id: number, event: string, name: string) => void;
      }
    )?.ym(24440456, 'reachGoal', 'save_workplace');
  }
};
</script>

<template>
  <div :class="$style.article">
    <ArticleBannerBoundary :index="props.index" />

    <div :class="$style.article__info">
      <ButtonBack v-if="!props.hideBack" :class="$style.article__info__back" />
      <div :class="$style.article__info__wrapper">
        <ArticleCategories
          v-if="props.item.category"
          :categories="[
            {
              title: props.item.category.name,
              link: `/category/${props.item.category.id}`,
            },
          ]"
        />
        <div :class="$style.article__info__date">
          {{ formatDate('fullDate', props.item.publication_date) }}
        </div>
        <!-- <ViewsCounter :views="props.item.views" /> -->
      </div>
    </div>

    <!-- Статья FORMA -->
    <template v-if="props.item.id === 100121">
      <div :class="$style.forma__articleAuthor">
        <div :class="$style.forma__articleAuthorLeft">
          <div :class="$style.forma__articleAuthorImage" />
        </div>
        <div :class="$style.forma__articleAuthorRight">
          <div :class="$style.forma__articleAuthorName">Евгения Борзова</div>
          <div :class="$style.forma__articleAuthorJob">
            Директор по коммерческой недвижимости FORMA
          </div>
        </div>
      </div>
    </template>
    <!-- Статья FORMA -->

    <h1 :class="$style.article__title">{{ props.item.title }}</h1>

    <ArticleShare :id="props.item.id" :type="props.item.type" />

    <ArticleImage
      v-if="
        props.item?.image?.photo.file_url &&
        !(isMaterialDTO(props.item) && props.item.is_gallery)
      "
      :image="props.item.image.photo.file_url"
      :source="props.item.image.owner"
      :alt="props.item.image.description"
      :image-width="(props.item as MaterialDTO).main_image_width"
      :image-height="(props.item as MaterialDTO).main_image_height"
    />
    <ArticleGallery
      v-if="
        isMaterialDTO(props.item) &&
        props.item?.is_gallery &&
        props.item?.gallery?.[0]
      "
      :gallery="props.item?.gallery?.[0]"
    />

    <ArticleText
      v-if="(props.item as MaterialDTO).subject"
      :class="$style.article__excerpt"
      :text="(props.item as MaterialDTO).subject"
    />

    <InterscrollerBanner v-if="!props.hideBanner" />

    <ArticleText
      :class="$style.article__text"
      :text="props.item.text"
      @click="handleClickFromWysiwyg"
    >
      <ArticleBannerMiddle
        v-if="![100121].includes(props.item.id)"
        :index="props.index"
      />
    </ArticleText>

    <ArticleSummary
      :item="(props.item as MaterialDTO).author || null"
      :person="
        props.item.person ?? (props.item as MaterialDTO).persons?.[0] ?? null
      "
      :category-name="(props.item as MaterialDTO)?.category?.name || false"
      :category-link="
        (props.item as MaterialDTO)?.category?.id
          ? `/category/${(props.item as MaterialDTO).category.id}`
          : false
      "
      :sectors="props.item.sectors"
      :ai-tags="props.item.ai_tags"
    />

    <SpecialProjectCre35Teaser
      :class="$style.article__cre35"
      placement="inner"
    />

    <SpecialProjectCmwp :class="$style.article__cmwp" placement="inner" />

    <ArticleRelative
      v-if="props.item.related_news?.length"
      :class="$style.article__relative"
      :slides="props.item.related_news"
    />
    <template v-else-if="props.item.news">
      <ArticleRelatedNews
        :title="props.item.news.title"
        :subject="props.item.news.subject"
        :category="props.item.news.category?.name ?? ''"
        :category-link="`/category/${props.item.news.category?.id}`"
        :link="getMaterialLink(props.item.news)"
        :image="props.item.news.image?.photo?.file_url"
      />
    </template>

    <ArticleSpecial />
  </div>
</template>

<style module lang="scss">
.article {
  display: flex;
  flex-flow: column;
  gap: var(--space-2x);
  width: 860px;
  margin-left: auto;
  margin-right: auto;
  border-bottom: 1px solid var(--color-gray-line);

  &__cre35,
  &__cmwp,
  &__relative {
    margin-left: -110px;
    margin-right: -110px;
  }

  &__info {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-2-5x);
    width: 100%;

    &__wrapper {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      gap: 20px;
    }

    &__date {
      @include text_caption1_caps;
      color: var(--color-gray-text);
      align-items: center;
    }
  }

  &__title {
    @include text_h1;
    color: var(--color-black-1);
    margin-top: 0;
    margin-bottom: 0;
  }

  &__excerpt {
    margin-top: var(--space-1x);
    margin-bottom: var(--space-3x);
    @include text_article_body1_medium;
  }

  &__newsItem {
    border: 1px solid var(--color-gray-line);
    margin-bottom: var(--space-3x);
    margin-top: var(--space-3x);
  }
}

.forma {
  &__article {
    &Author {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      gap: 20px;

      &Image {
        width: 60px;
        height: 60px;
        background: url('/forma/borzova.png') no-repeat;
        background-size: cover;
        border-radius: 50%;
      }

      &Right {
        display: flex;
        flex-flow: column nowrap;
        gap: 4px;
      }

      &Name {
        font-weight: 500;
        font-size: 18px;
        line-height: 120%;
        letter-spacing: 0%;
        color: var(--color-black-1);
      }

      &Job {
        font-weight: 400;
        font-size: 14px;
        line-height: 140%;
        letter-spacing: 0%;
        color: rgba(153, 153, 153, 1);
      }
    }
  }
}

@media (max-width: 1379px) {
  .article {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
    border-bottom: none;

    &__cre35,
    &__cmwp,
    &__relative {
      margin-left: -20px;
      margin-right: -20px;
    }
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .article {
    &__title {
      @include text_h2;
    }
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .article {
    &__info {
      gap: var(--space-4x);

      &__wrapper {
        justify-content: space-between;
        width: inherit;
      }
    }
  }
}

@media (max-width: 767px) {
  .article {
    &__title {
      @include text_h3;
    }
  }
}

@media (min-width: 480px) and (max-width: 599px) {
  .article {
    &__info {
      flex-direction: column;
      gap: var(--space-1-5x);

      &__back {
        width: 100%;
        justify-content: flex-start;
      }

      &__wrapper {
        justify-content: space-between;
        width: inherit;
      }
    }
  }
}

@media (max-width: 479px) {
  .article {
    &__info {
      flex-direction: row;
      gap: var(--space-1-5x);
      justify-content: space-between;
      align-items: flex-start;

      &__wrapper {
        flex-direction: column;
        gap: var(--space-0-5x);
        align-items: flex-end;
      }
    }

    &__slider {
      margin-left: -20px;
      margin-right: -20px;
    }
  }
}
</style>
